import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import PreviewCompatibleImage from '../components/PreviewCompatibleImage';
import servicesImg from '../img/Services.png';
import Layout from '../components/Layout';
import Seo from '../components/Seo';
import { HTMLContent } from '../components/Content';
import Accordion from '../components/Accordion/Accordion';

class AboutPageTemplate extends React.Component {
    accordionData = [{
      title: 'What kind of companies do you work with?',
      content: `Mostly startups and scaleups building AI into their product, often before they have an in-house AI team. I have particular experience in health tech, life sciences and defence tech, but I've also worked with companies in legal, logistics, retail and e-commerce.
      `,
    },
    {
      title: 'What is agentic AI?',
      content: `Agentic AI systems go beyond answering a single prompt. They can call tools, search your data, make decisions and carry out multi-step tasks.
      <br/>
      <br/>
      They're powerful, but making them reliable takes careful engineering, evaluation and monitoring, and that's most of what I do.
      `,
    },
    {
      title: 'Can your systems run without the cloud?',
      content: `Yes. Most systems I build run in the cloud, but some can't or shouldn't, because of sensitive data, regulation, inference costs, latency or a need to work offline. In those cases I deploy on on-premise GPU servers or on-device, using open-weight LLMs, speech recognition and embedding models.
      <br/>
      <br/>
      This gives you full control of your data and costs without giving up capability.
      `,
    },
    {
      title: 'How do you handle sensitive data?',
      content: `I've spent much of my career working with clinical and other sensitive data, so governance is built in from the start. That includes choosing where models run, what data leaves your systems, and how outputs are validated and audited.
      `,
    },
    {
      title: 'What is your process?',
      content: `I'm flexible in how I work. It usually starts with an initial call to understand your business, your product and what you're trying to achieve. If I'm a good fit, we scope the work together.
      <br/>
      <br/>
      Scoping usually means looking at your data, your existing systems and your users. You're the expert on your business, so I work closely with you during this phase. Then I build in short iterations, with regular demos so you can see progress and change direction early.
      <br/>
      <br/>
      I build systems that integrate with your existing infrastructure, and I document and hand them over properly so your team can own them.
      <br/>
      <br/>
      Any intellectual property created during our work together belongs to you. I don't sell licences or push you towards particular suppliers unless it's genuinely to your advantage.
      `,
    },
    {
      title: 'How do you charge?',
      content: `For a defined project, I usually charge a fixed fee. We agree up front what you need and what you'll get, so the cost is clear and predictable. I work with a lot of early-stage companies, and I know budgets are often tight.
      <br/>
      <br/>
      If you want an ongoing relationship, such as a fractional senior AI engineer for a few days a month, or you have ad hoc needs, I can work on a day rate or retainer instead.
      `,
    },
    {
      title: 'What if I just want some advice?',
      content: `That's fine. Many engagements start with a single strategy session or a technical review of something you've already built. <a href="https://www.ortom.ai/#footer">Get in touch</a> and we can have a chat.
      `,
    },
    {
      title: 'How can I find out more?',
      content: `Have a look at the <a href="https://www.ortom.ai/case-studies">case studies</a>, connect with me on <a href="https://www.linkedin.com/in/tomliptrot1/">LinkedIn</a>, or <a href="https://www.ortom.ai/#footer">get in touch</a>.
      `,
    },
    ];

    render() {
      // Build FAQ JSON-LD from the first 6 substantive Q&As
      const faqItems = this.accordionData.slice(0, 6);
      const faqJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map(item => ({
          '@type': 'Question',
          name: item.title,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.content.replace(/<br\s*\/?>/g, ' ').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim(),
          },
        })),
      };

      return (
        <div>
          <div className="container content mobile-container">
            <div className="columns">
              <div className="column is-2">
                <p>
                  What I do
                </p>
              </div>
              <div className="column is-10">
                <h2>Building agentic AI products that work in production.</h2>
              </div>
            </div>

            <div className="columns">
              <div className="column is-12">
                {servicesImg ? (
                  <div className="featured-thumbnail" style={{ textAlign: 'center' }}>
                    <PreviewCompatibleImage
                      imageInfo={{
                        image: servicesImg,
                        alt: 'Manchester skyline — Ortom AI consulting services',
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </div>

            <div className="small-gap" />

            <div className="column is-8 is-offset-2">
              <p>
                I help startups and scaleups build agentic AI and LLM systems
                that work in production. I&apos;m often the first senior AI hire
                a company has, on a flexible basis. I help founders make the right
                technical calls early, build the core system, and then hand over
                to an in-house team when the time is right.
              </p>
            </div>

            <div className="column is-10 is-offset-2">
              <h3>Production agentic AI systems</h3>
            </div>

            <div className="column is-7 is-offset-3">
              <p>
                Building a demo with an LLM takes an afternoon. Building an agent
                you can trust with real users, real data and real consequences
                is a different job.
              </p>

              <p>
                I design and build:
              </p>
              <ul>
                <li>
                  Agentic workflows that use tools, make decisions and carry out
                  multi-step tasks, with guardrails and human oversight where it matters
                </li>
                <li>RAG systems that answer questions from your own documents, with sources</li>
                <li>
                  Structured extraction that turns unstructured text, documents or
                  speech into validated data
                </li>
                <li>LLM integrations embedded in your existing product</li>
              </ul>

              <p>
                I take reliability seriously. That means typed, validated outputs,
                proper evaluation, observability, and clear behaviour when things
                go wrong. My core stack is Python, FastAPI and PydanticAI across
                AWS, GCP and Azure.
              </p>

              <p>
                <strong>Deployed where you need it.</strong>
                {' '}
                Most systems run in the cloud. Some can&apos;t, or shouldn&apos;t.
                That might be because of sensitive data, regulation, inference costs,
                latency or working offline. In those cases I deploy on-premise GPU
                servers or on-device models using open-weight LLMs, speech recognition
                and embedding models. This gives you full control of your data and
                costs without giving up capability.
              </p>
            </div>

            <div className="small-gap" />

            <div className="column is-10 is-offset-2">
              <h3>AI strategy and technical due diligence</h3>
            </div>

            <div className="column is-7 is-offset-3">
              <p>
                Early technical decisions are expensive to undo. I help founders
                work out what&apos;s realistic, what to build first, and whether
                to build, buy or use an existing model. I also carry out technical
                due diligence for investors assessing AI startups, and help
                founders prepare for it.
              </p>
              <p>
                Typical questions I help with:
              </p>
              <ul>
                <li>What can AI realistically do for our product?</li>
                <li>Should we build our own, buy, or use an existing model?</li>
                <li>Cloud, on-premise or on-device, and what are the trade-offs?</li>
                <li>Do we have the right data, skills and infrastructure?</li>
                <li>What should we build first, how long will it take, and what will it cost?</li>
              </ul>
            </div>

            <div className="small-gap" />

            <div className="column is-10 is-offset-2">
              <h3>Team building and mentoring</h3>
            </div>

            <div className="column is-7 is-offset-3">
              <p>
                When you&apos;re ready to build an in-house AI team, I can help
                you hire, assess candidates and mentor your first data scientists
                and ML engineers. That way the system I&apos;ve built is in good
                hands.
              </p>
            </div>

            <div className="small-gap" />

            <div className="column is-10 is-offset-2">
              <h3>Who I work with</h3>
            </div>

            <div className="column is-7 is-offset-3">
              <p>
                Mostly startups and scaleups, with specialist experience in:
              </p>
              <ul>
                <li>
                  <strong>Health tech and life sciences:</strong>
                  {' '}
                  clinical NLP, medical speech recognition, patient-facing
                  conversational AI and predictive modelling for drug discovery
                </li>
                <li>
                  <strong>Defence tech:</strong>
                  {' '}
                  AI for constrained, disconnected and high-assurance environments,
                  including local speech and language models for field use
                </li>
                <li>
                  <strong>Other sectors:</strong>
                  {' '}
                  legal, logistics, retail and e-commerce
                </li>
              </ul>
            </div>

            <div className="small-gap" />

          </div>

          <div className="container content mobile-container">
            <div className="accordion">
              {this.accordionData.map(({ title, content }, index) => (
                <Accordion title={title} content={content} key={index} />
              ))}
            </div>
          </div>

          <div className="gap" />

          <script type="application/ld+json">
            {JSON.stringify(faqJsonLd)}
          </script>
        </div>
      );
    }
}

const AboutPage = ({ data }) => {
  const { markdownRemark: post } = data;

  return (
    <Layout>
      <Seo
        title="Agentic AI & LLM Development for Startups | Ortom, Manchester"
        description="Production agentic AI and LLM systems for startups and scaleups, deployed in the cloud, on-premise or on-device. Plus AI strategy and team building. Based in Manchester."
        pathname="/services/"
      />
      <AboutPageTemplate
        contentComponent={HTMLContent}
        title={post.frontmatter.title}
        content={post.html}
      />
    </Layout>
  );
};

AboutPage.propTypes = {
  data: PropTypes.object.isRequired,
};

export default AboutPage;

export const aboutPageQuery = graphql`
  query AboutPage($id: String!) {
    markdownRemark(id: { eq: $id }) {
      html
      frontmatter {
        title
      }
    }
  }
`;
